// Autocomplete de empresas — ignora acentos, coincide por palabras en cualquier orden.
// Backend: GET /api/entidades/autocomplete?q=...
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Building2, Loader2 } from 'lucide-react';
import { entidadesApi, resolveAssetUrl } from '../../services/api';

const EmpresaAutocomplete = ({
  value,
  onChange,
  placeholder = 'Buscar por nombre, CIF, localidad…',
  onPick,
  autoNavigate = false,
  testid = 'empresa-autocomplete',
}) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const navigate = useNavigate();
  const ref = useRef(null);
  const debounceRef = useRef(null);

  // Debounced search
  useEffect(() => {
    if (!value || value.trim().length < 2) {
      setItems([]);
      return;
    }
    setLoading(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await entidadesApi.autocomplete(value, 10);
        setItems(r.items || []);
        setHighlight(0);
      } catch { setItems([]); }
      finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(debounceRef.current);
  }, [value]);

  // Cerrar al hacer click fuera
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const elegir = (it) => {
    setOpen(false);
    if (onPick) onPick(it);
    if (autoNavigate) navigate(`/clientes/${it.id}`);
  };

  const onKey = (e) => {
    if (!open || items.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => Math.min(h + 1, items.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); elegir(items[highlight]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div className="relative" ref={ref} data-testid={testid}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
        <input
          type="text"
          value={value}
          placeholder={placeholder}
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          className="w-full h-10 pl-10 pr-10 rounded-md border border-ink-200 bg-white text-sm focus:border-brand-400 focus:ring-1 focus:ring-brand-200 outline-none"
          data-testid={`${testid}-input`}
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 animate-spin" />}
      </div>
      {open && items.length > 0 && (
        <div
          className="absolute z-40 left-0 right-0 mt-1 bg-white border border-ink-200 rounded-md shadow-lg max-h-80 overflow-auto"
          data-testid={`${testid}-dropdown`}
        >
          {items.map((it, i) => (
            <button
              key={it.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); elegir(it); }}
              onMouseEnter={() => setHighlight(i)}
              className={`w-full text-left px-3 py-2 flex items-start gap-3 border-b border-ink-100 last:border-0 ${
                i === highlight ? 'bg-brand-50' : 'hover:bg-ink-50'
              }`}
              data-testid={`${testid}-item-${i}`}
            >
              <div className="h-8 w-8 rounded bg-ink-100 text-ink-500 flex items-center justify-center shrink-0">
                {it.logo_url ? <img src={resolveAssetUrl(it.logo_url)} alt="" className="h-full w-full object-contain rounded" /> : <Building2 className="w-4 h-4" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-ink-800 truncate">
                  {it.razon_social}
                  {it.nombre_comercial && it.nombre_comercial !== it.razon_social && (
                    <span className="text-ink-400 font-normal"> · {it.nombre_comercial}</span>
                  )}
                </div>
                <div className="text-xs text-ink-500 truncate">
                  {[it.cif, it.codigo_cliente_erp && `ERP ${it.codigo_cliente_erp}`, it.localidad, it.provincia].filter(Boolean).join(' · ') || '—'}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
      {open && !loading && value.trim().length >= 2 && items.length === 0 && (
        <div
          className="absolute z-40 left-0 right-0 mt-1 bg-white border border-ink-200 rounded-md shadow-lg px-3 py-3 text-sm text-ink-500"
          data-testid={`${testid}-empty`}
        >
          Sin coincidencias para <strong>{value}</strong>.
        </div>
      )}
    </div>
  );
};

export default EmpresaAutocomplete;
